import React, { useState, useEffect } from "react";
import { SurveyCreatorComponent, SurveyCreator } from "survey-creator-react";
import { registerCreatorTheme, editorLocalization } from "survey-creator-core";
import * as italian from "survey-creator-core/i18n/italian";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Snackbar, Alert } from "@mui/material";
import surveyService from "../services/survey-service";
import DashboardLayout from "../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../examples/Navbars/DashboardNavbar";
import MDBox from "../components/MDBox";
import MDTypography from "../components/MDTypography";
import MDButton from "../components/MDButton";
import "survey-core/survey-core.min.css";
import "survey-creator-core/survey-creator-core.min.css";
import { creatorConfig, customTheme } from "./utils";

const defaultFields = [
	{
		type: "dropdown",
		name: "categoria",
		title: "Categoria",
		isRequired: true,
		choices: ["A", "B", "C"],
	},
];

const creator = new SurveyCreator(creatorConfig);

registerCreatorTheme(customTheme);
creator.applyCreatorTheme(customTheme);

creator.JSON = {
	pages: [
		{
			name: "Pagina 1",
			elements: defaultFields,
		},
	],
};

creator.onQuestionAdded.add((sender, options) => {
	const defaultNames = ["categoria"];
	if (defaultNames.includes(options.question.name)) {
		options.question.allowDelete = false;
		options.question.allowRename = false;
	}
});

editorLocalization.currentLocale = "it";

export default function SurveyBuilder() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(!!id);
	const [notification, setNotification] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const [creator, setCreator] = useState(null);

	useEffect(() => {
		const newCreator = new SurveyCreator({
			showThemeTab: false,
			showCreatorThemeSettings: false,
			showJSONEditorTab: false,
			showLogicTab: false,
			previewAllowSimulateDevices: false,
		});

		const customTheme = {
			themeName: "bottoli",
			cssVariables: {
				"--sjs-layer-1-background-500": "#FFFFFFFF",
				"--sjs-layer-1-background-400": "#FFFFFFFF",
				"--sjs-layer-1-foreground-100": "#122243FF",
				"--sjs-layer-1-foreground-50": "#0E205580",
				"--sjs-layer-3-background-500": "#FFFFFFFF",
				"--sjs-layer-3-foreground-100": "#273859FF",
				"--sjs-layer-3-foreground-50": "#26335880",
				"--sjs-layer-2-background-500": "#FFFFFFFF",
				"--sjs-layer-2-background-400": "#FFFFFFFF",
				"--sjs-special-haze": "#3189EC40",
				"--sjs-border-25": "#3189EC20",
				"--sjs-border-10": "#3189EC15",
				"--sjs-primary-background-500": "#3189ECFF",
				"--sjs-primary-background-10": "#3189EC1A",
				"--sjs-primary-background-400": "#2070D6FF",
				"--sjs-primary-foreground-100": "#FFFFFFFF",
				"--sjs-primary-foreground-25": "#FFFFFF40",
				"--sjs-secondary-background-500": "#3189ECFF",
				"--sjs-secondary-background-25": "#3189EC40",
				"--sjs-secondary-background-10": "#3189EC1A",
				"--sjs-secondary-foreground-100": "#FFFFFFFF",
				"--sjs-secondary-forecolor-25": "#FFFFFF40",
				"--sjs-semantic-red-background-500": "#D6378AFF",
				"--sjs-semantic-red-background-10": "#D6378A1A",
				"--sjs-semantic-red-foreground-100": "#FFFFFFFF",
				"--sjs-semantic-green-background-500": "#19B394FF",
				"--sjs-semantic-green-background-10": "#19B3941A",
				"--sjs-semantic-green-foreground-100": "#FFFFFFFF",
				"--sjs-semantic-blue-background-500": "#3189ECFF",
				"--sjs-semantic-blue-background-10": "#3189EC1A",
				"--sjs-semantic-blue-foreground-100": "#FFFFFFFF",
				"--sjs-semantic-yellow-background-500": "#FF9814FF",
				"--sjs-semantic-yellow-background-10": "#FF98141A",
				"--sjs-semantic-yellow-foreground-100": "#FFFFFFFF",
				"--sjs-semantic-white-background-500": "#FFFFFFFF",
				"--sjs-code-gray-700": "#B6B6B6FF",
				"--sjs-code-blue-500": "#3189ECFF",
				"--sjs-code-gray-300": "#505050FF",
				"--sjs-code-green-500": "#08997CFF",
				"--sjs-code-red-500": "#F41B50FF",
				"--sjs-code-purple-500": "#C22FA2FF",
				"--sjs-code-yellow-500": "#F58D06FF",
				"--sjs-code-gray-500": "#8A8A8AFF",
				"--sjs-special-background": "#FFFFFFFF",
				"--sjs-layer-1-foreground-75": "#3189ECFF",
				"--sjs-layer-3-background-400": "#FFFFFFFF",
				"--sjs-special-glow": "#3189EC33",
				"--sjs-special-shadow": "#3189EC40",
				"--sjs-layer-3-foreground-75": "#3189ECFF",
				"--sjs-layer-2-foreground-100": "#273859FF",
				"--sjs-layer-2-foreground-75": "#3189ECFF",
				"--sjs-layer-2-foreground-50": "#26335880",
				"--sjs-border-25-overlay": "#00000026",
				"--sjs-secondary-background-400": "#2070D6FF",
				"--sjs-primary-backcolor": "#3189EC",
			},
		};

		registerCreatorTheme(customTheme);
		newCreator.applyCreatorTheme(customTheme);
		editorLocalization.currentLocale = "it";

		newCreator.saveSurveyFunc = async (saveNo, callback) => {
			try {
				const json = newCreator.JSON;

				if (id) {
					await surveyService.updateSurvey(id, { jsonField: json });
					showNotification("Sondaggio aggiornato con successo", "success");
				} else {
					await surveyService.createSurvey({ jsonField: json });
					showNotification("Sondaggio salvato con successo", "success");
				}
				callback(saveNo, true);
			} catch (error) {
				console.error("Error saving survey:", error);
				showNotification("Errore nel salvataggio del sondaggio", "error");
				callback(saveNo, false);
			}
		};

		setCreator(newCreator);

		if (id) {
			loadSurvey(id, newCreator);
		} else {
			setLoading(false);
		}
	}, [id]);

	const loadSurvey = async (surveyId, creatorInstance) => {
		try {
			const response = await surveyService.getSurveyById(surveyId);
			const surveyJson =
				typeof response.jsonField === "string" ? JSON.parse(response.jsonField) : response.jsonField;
			creatorInstance.text = JSON.stringify(surveyJson);
		} catch (error) {
			console.error("Error loading survey:", error);
			showNotification("Errore nel caricamento del sondaggio", "error");
		} finally {
			setLoading(false);
		}
	};

	const showNotification = (message, severity) => {
		setNotification({ open: true, message, severity });
	};

	const handleBackToList = () => {
		navigate("/survey-creator/list");
	};

	if (loading) {
		return (
			<DashboardLayout>
				<DashboardNavbar />
				<MDBox py={3} display="flex" justifyContent="center">
					<MDTypography variant="h6">Caricamento...</MDTypography>
				</MDBox>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox py={3}>
				<MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
					<MDBox>
						<MDTypography variant="h4" fontWeight="medium">
							{id ? "Modifica Sondaggio" : "Nuovo Sondaggio"}
						</MDTypography>
						<MDTypography variant="body2" color="text">
							{id ? "Modifica il tuo sondaggio esistente" : "Crea un nuovo sondaggio personalizzato"}
						</MDTypography>
					</MDBox>
					<MDButton variant="outlined" color="secondary" onClick={handleBackToList}>
						Torna alla Lista
					</MDButton>
				</MDBox>

				<Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
					{creator && <SurveyCreatorComponent creator={creator} creatorTheme={creator.theme} />}
				</Box>
			</MDBox>
		</DashboardLayout>
	);
}
